import { useEffect, useState } from "react";
import { t } from "@/i18n-js/instance";
import { SettingsHeader } from "@/react/components/SettingsApp/SettingsHeader";
import { usePunditUserContext } from "@/react/contexts";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import {
  USAGE_SECTIONS,
  UsageContextProvider,
} from "@circle-react/providers/UsageContext";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { Tab } from "@circle-react-uikit/TabV2";
import { FiltersBar } from "./FiltersBar";
import { LandingPage } from "./LandingPage";
import { MobileDashboard } from "./MobileDashboard";
import { NoAgents } from "./NoAgents";
import { SnippetsV2 } from "./SnippetsV2";
import { SpacesV2 } from "./SpacesV2";
import { useIndexAgents } from "./agentsQueries";
import {
  COMMUNITY_TYPE_OPTIONS,
  CUSTOM_TYPE_OPTIONS,
  TABS,
  TABS_LABELS,
} from "./constants";

const tabNames = Object.values(TABS);

export const CommunityBotKnowledge = () => {
  const { data: agentsQuery, isLoading } = useIndexAgents({ currentPage: 1 });
  const isMobileView = useSmOrMdOrLgScreenMediaQuery();
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    tabNames.indexOf(TABS.TAB_COMMUNITY),
  );

  const [filters, setFilters] = useState({
    name: "",
    type: "",
    spaceGroup: "",
  });

  const { currentCommunity } = usePunditUserContext();

  const hasNoAgents = !agentsQuery?.records || agentsQuery.records.length === 0;

  const shouldShowLandingPage =
    !currentCommunity?.ai_agents_feature_flag_enabled && hasNoAgents;

  const shouldShowEmptyState =
    Boolean(currentCommunity?.ai_agents_feature_flag_enabled) && hasNoAgents;

  useEffect(() => {
    setFilters({
      name: "",
      type: "",
      spaceGroup: "",
    });
  }, [selectedTabIndex]);

  const renderFilterBar = (tabIndex: number) => {
    const isCommunityTab = tabIndex === tabNames.indexOf(TABS.TAB_COMMUNITY);
    const typeOptionsToUse = isCommunityTab
      ? COMMUNITY_TYPE_OPTIONS
      : CUSTOM_TYPE_OPTIONS;

    return (
      <FiltersBar
        name={filters.name}
        onNameChange={name => setFilters({ ...filters, name })}
        type={filters.type}
        onTypeChange={type => setFilters({ ...filters, type })}
        typeOptions={typeOptionsToUse}
        spaceGroup={filters.spaceGroup}
        onSpaceGroupChange={spaceGroup =>
          setFilters({ ...filters, spaceGroup })
        }
        showSpaceGroup={isCommunityTab}
      />
    );
  };

  if (isMobileView) {
    return <MobileDashboard />;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (shouldShowLandingPage) {
    return (
      <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
        <LandingPage />
      </UsageContextProvider>
    );
  }

  if (shouldShowEmptyState) {
    return (
      <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
        <NoAgents />
      </UsageContextProvider>
    );
  }

  return (
    <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
      <div className="bg-primary h-full">
        <Tab.Group
          className="!h-auto overflow-visible"
          selectedIndex={selectedTabIndex}
          onChange={tabIndex => setSelectedTabIndex(tabIndex)}
        >
          <SettingsHeader
            pageTitle={t("settings.community_bot.knowledge.title")}
            hasBorder={false}
            filters={
              <Tab.ListWrapper className="lg:px-9">
                <Tab.List>
                  {tabNames.map(tab => (
                    <Tab key={tab}>{TABS_LABELS[tab]}</Tab>
                  ))}
                </Tab.List>
              </Tab.ListWrapper>
            }
          />

          <Tab.Panels>
            <Tab.Panel>
              {renderFilterBar(selectedTabIndex)}
              <SpacesV2
                nameFilter={filters.name}
                typeFilter={filters.type}
                spaceGroupFilter={filters.spaceGroup}
              />
            </Tab.Panel>
            <Tab.Panel>
              {renderFilterBar(selectedTabIndex)}
              <SnippetsV2 nameFilter={filters.name} typeFilter={filters.type} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </UsageContextProvider>
  );
};
